import React, { useEffect, useState } from 'react';
import { Router, Route, Switch } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Loadable from 'react-loadable';
import mixpanel from 'mixpanel-browser';
import { Helmet } from 'react-helmet';
import { trackEvent } from './library/event_tracking/EventTracking';
import { TrackedEvents } from './library/event_tracking/TrackingEnums';
import isExcludedUrl from './library/event_tracking/TrackingExclude';

import './App.scss';
import { initAction, logoutAction } from './store/actions/accountAction';
import { HitchpinApi } from './api/Hitchpin';
import { useProfile } from './store/reducers/profile';

const initializeGtmContainer = () => {
  const productionHost = 'hitchpin.com';
  const testingToken = 'GTM-NTT7Q6S';
  const prodToken = 'GTM-W2KQC5J';
  const token =
    window.location.hostname.toLowerCase() === productionHost ||
    window.location.hostname.toLowerCase() === 'www.hitchpin.com'
      ? prodToken
      : testingToken;
  // eslint-disable-next-line
  (function (w, d, s, l, i) {
    // eslint-disable-next-line
    w[l] = w[l] || [];
    w[l].push({
      'gtm.start':
        // eslint-disable-next-line
        new Date().getTime(),
      event: 'gtm.js',
    }); // eslint-disable-next-line
    var f = d.getElementsByTagName(s)[0],
      // eslint-disable-next-line
      j = d.createElement(s), // eslint-disable-next-line
      dl = l != 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src =
      // eslint-disable-next-line
      'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f);
    // eslint-disable-next-line
  })(window, document, 'script', 'dataLayer', token);
};

const loading = () => (
  <div className="animated fadeIn pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse" />
  </div>
);

// Containers
const DefaultLayout = Loadable({
  loader: () => import('./page_layout/CoreUiDefaultLayout/CoreUiDefaultLayout'),
  loading,
});

const Login = Loadable({
  loader: () => import('./pages/Auth/LoginPage'),
  loading,
});

const PasswordReset = Loadable({
  loader: () => import('./pages/Auth/PasswordReset'),
  loading,
});

const PasswordResetKey = Loadable({
  loader: () => import('./pages/Auth/PasswordResetKey'),
  loading,
});

const SetPasswordForFirstTime = Loadable({
  loader: () => import('./pages/Auth/SetPasswordForFirstTime'),
  loading,
});

const App = props => {
  const profile = useProfile.get();
  const dispatch = useDispatch();
  const [profileLock, setProfileLock] = useState(false);
  const [gtmContainerLock, setGtmContainerLock] = useState(false);

  useEffect(() => {
    if (!isExcludedUrl(window.location.pathname)) {
      trackEvent(TrackedEvents.PageVisited);
    }

    const unlisten = props.history.listen(() => {
      if (!isExcludedUrl(window.location.pathname)) {
        trackEvent(TrackedEvents.PageVisited);
      }
    });

    return () => unlisten();
  }, [props.history]);

  useEffect(() => {
    if (localStorage.getItem('token')) {
      dispatch(initAction());
    } else {
      dispatch(logoutAction());
      dispatch(initAction());
    }
  }, [dispatch]);

  useEffect(() => {
    if (localStorage.getItem('token')) {
      if (!profileLock) {
        setProfileLock(true);

        // This grabs the user object, not profile object
        HitchpinApi.auth
          .get()
          .then(res => {
            mixpanel.identify(res.body.id);
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              userId: res.body.id,
              event: 'userIdSet',
            });
            setGtmContainerLock(true);
          })
          .catch(() => setProfileLock(false));
      }
    } else {
      setGtmContainerLock(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  useEffect(() => {
    if (gtmContainerLock) {
      initializeGtmContainer();
    }
  }, [gtmContainerLock]);

  return (
    <>
      <Helmet defaultTitle="HitchPin" titleTemplate="%s">
        <meta property="fb:app_id" content="318400175485400" />
      </Helmet>
      <Router history={props.history}>
        <Switch>
          <Route
            exact
            path="/accounts/password/reset"
            name="Password Reset"
            component={PasswordReset}
          />
          <Route
            exact
            path="/accounts/password/reset/:uid/:token"
            name="Password Reset"
            component={PasswordResetKey}
          />
          <Route
            exact
            path="/accounts/login"
            name="Login Page"
            component={Login}
          />
          <Route
            exact
            path="/accounts/setup/:url_safe_base_sixty_four_encoded_pk/:token/:email"
            name="Account Setup Redirect"
            component={SetPasswordForFirstTime}
          />
          <Route path="/" name="Home" component={DefaultLayout} />
        </Switch>
      </Router>
    </>
  );
};

export default App;
